/**
 * Utility functions for URL handling
 */

import { logWarning } from "./errorLogger";

/**
 * Generate a URL-friendly slug from an event title and date
 * Converts "Photography Exhibition Launch" + "2024-01-15" to "Photography-Exhibition-Launch-2024-01-15"
 * Handles Arabic and Unicode characters by encoding them
 * Includes date and short ID to ensure uniqueness even with duplicate titles
 */
export const generateEventSlug = (
  title: string,
  eventDate?: string,
  eventId?: string
): string => {
  if (!title || typeof title !== "string") {
    return "";
  }

  const trimmed = title.trim();
  if (!trimmed) {
    return "";
  }

  // Get short ID from event UUID (first 8 chars) for uniqueness
  const shortId = eventId ? eventId.substring(0, 8) : "";
  const idSuffix = shortId ? `-${shortId}` : "";

  // Format date as YYYY-MM-DD if provided
  let dateSuffix = "";
  if (eventDate) {
    try {
      // Parse the date and format it consistently
      const date = new Date(eventDate);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        dateSuffix = `-${year}-${month}-${day}`;
      }
    } catch (e) {
      // If date parsing fails, continue without date suffix
      logWarning("Failed to parse event date for slug", { eventDate });
    }
  }

  // Check if title contains non-ASCII characters (Arabic, etc.)
  const hasNonASCII = /[^\u0020-\u007E]/.test(trimmed);

  if (hasNonASCII) {
    // For non-ASCII characters, encode them properly for URL
    // Replace spaces with dashes, then encode
    const titleSlug = trimmed.replace(/\s+/g, "-");
    const encoded = encodeURIComponent(titleSlug);
    // Ensure we have a valid slug (not empty after encoding)
    const baseSlug = encoded || "event";
    return baseSlug + idSuffix + dateSuffix;
  }

  // For ASCII-only titles, use simple slug format
  const slug = trimmed
    .replace(/\s+/g, "-") // Replace spaces with dashes
    .replace(/[^\w-]/g, "") // Remove special characters except dashes
    .replace(/--+/g, "-") // Replace multiple dashes with single dash
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing dashes

  // Ensure slug is never empty (fallback to 'event' if all characters were stripped)
  const baseSlug = slug || "event";
  return baseSlug + idSuffix + dateSuffix;
};

/**
 * Generate event view URL using the secure slug from the event object.
 * The slug is generated on the backend from the event ID and is URL-safe.
 * Falls back to generating slug from title if slug is not available (for backward compatibility).
 */
export const generateEventViewUrl = (
  eventId: string,
  eventTitle: string,
  eventDate?: string,
  userRole?: string,
  eventSlug?: string
): string => {
  // Use the slug from the event object if available (preferred method)
  // Otherwise fall back to generating from title (for backward compatibility with old events)
  const slug = eventSlug || generateEventSlug(eventTitle || "", eventDate, eventId);
  const rolePrefix = userRole === "admin" ? "/admin" : `/${userRole}`;

  // Use the secure slug - not the event ID for security
  const url = `${rolePrefix}/events/${slug}`;

  return url;
};

/**
 * Extract event ID from URL path parameters
 */
export const extractEventIdFromUrl = (pathname: string): string | null => {
  const pathParts = pathname.split("/");
  const eventsIndex = pathParts.findIndex((part) => part === "events");
  const eventId =
    eventsIndex !== -1 && pathParts.length > eventsIndex + 1
      ? pathParts[eventsIndex + 1]
      : null;

  return eventId;
};

/**
 * Extract event slug from URL path parameters (for backward compatibility)
 */
export const extractEventSlugFromUrl = (pathname: string): string | null => {
  const pathParts = pathname.split("/");
  const eventsIndex = pathParts.findIndex((part) => part === "events");
  // For new format: /events/{id}/{slug}, slug is at index + 2
  // For old format: /events/{slug}, slug is at index + 1
  const slug =
    eventsIndex !== -1 && pathParts.length > eventsIndex + 2
      ? pathParts[eventsIndex + 2] // New format with ID
      : pathParts.length > eventsIndex + 1
      ? pathParts[eventsIndex + 1]
      : null; // Old format

  // Debug logging (remove in production)
  return slug;
};

/**
 * Convert slug back to searchable title format
 * Removes date suffix (YYYY-MM-DD) if present
 */
export const slugToTitle = (slug: string): string => {
  if (!slug) {
    return "";
  }

  // Remove date suffix pattern: -YYYY-MM-DD at the end
  // Match: - followed by 4 digits, dash, 2 digits, dash, 2 digits at end of string
  const slugWithoutDate = slug.replace(/-\d{4}-\d{2}-\d{2}$/, "");

  try {
    // Try to decode if it's URL-encoded (for Arabic/Unicode)
    const decoded = decodeURIComponent(slugWithoutDate);
    return decoded.replace(/-/g, " ");
  } catch (e) {
    // If decoding fails, it's probably ASCII slug format
    return slugWithoutDate.replace(/-/g, " ");
  }
};

/**
 * Extract date from slug (if present)
 * Returns date in YYYY-MM-DD format or null
 */
export const extractDateFromSlug = (slug: string): string | null => {
  if (!slug) {
    return null;
  }

  // Match date pattern: -YYYY-MM-DD at the end
  const dateMatch = slug.match(/-(\d{4}-\d{2}-\d{2})$/);
  if (dateMatch) {
    return dateMatch[1]; // Returns YYYY-MM-DD
  }

  return null;
};
